const LIVE_ELIGIBLE_AGENT_PROVIDERS = ['gemini-2.5-pro', 'gemini-3-pro-preview'];
const LIVE_ELIGIBLE_VARIANTS = ['with_news'];
const PROVIDER_DISPLAY_NAMES = {
  'gemini-2.5-pro': 'Gemini 2.5 Pro',
  'gemini-3-pro-preview': 'Gemini 3 Pro Preview',
  'claude-sonnet-4-5@20250929': 'Claude Sonnet 4.5',
  'claude-opus-4-5@20250929': 'Claude Opus 4.5',
  'openrouter:deepseek/deepseek-r1': 'DeepSeek R1',
  'openrouter:openai/gpt-5': 'GPT-5',
  'openrouter:qwen/qwen3-max': 'Qwen 3 Max',
  'openrouter:moonshotai/kimi-k2-thinking': 'Kimi K2 Thinking',
};
const VARIANT_LABELS = {
  plain: 'Open / Close · plain',
  with_news: 'Open / Close · with news',
  with_twitter_list: 'Twitter list benchmark',
};
const KNOWN_VARIANT_SUFFIXES = ['with_twitter_list', 'with_news', 'plain'];

const EASTERN_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const USD_INTEGER_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function normalizeProviderKey(llmProvider = '') {
  const normalizedProvider = `${llmProvider}`.trim();

  for (const suffix of KNOWN_VARIANT_SUFFIXES) {
    const suffixToken = `-${suffix}`;
    if (normalizedProvider.endsWith(suffixToken)) {
      return {
        agentProvider: normalizedProvider.slice(0, normalizedProvider.length - suffixToken.length),
        variant: suffix,
      };
    }
  }

  return {
    agentProvider: normalizedProvider,
    variant: 'plain',
  };
}

export function formatEasternTimestamp(value) {
  const date = coerceDate(value);
  if (!date) {
    return null;
  }

  return `${EASTERN_DATE_FORMATTER.format(date)} ET`;
}

export function formatLiveMetric({ notionalPnl = 0, sharpeRatio = 0 }) {
  const sign = notionalPnl > 0 ? '+' : notionalPnl < 0 ? '-' : '';
  const absolutePnl = USD_INTEGER_FORMATTER.format(Math.abs(notionalPnl));
  return `P&L ${sign}${absolutePnl} · Sharpe ${sharpeRatio.toFixed(2)}`;
}

export function aggregateLeaderboardRows(trades = [], options = {}) {
  const maxRows = options.maxRows || 4;
  const groupedRows = new Map();

  trades.forEach(trade => {
    if (!trade || trade.status !== 'closed' || typeof trade.pnl !== 'number') {
      return;
    }

    const updatedAt = coerceDate(trade.closeTime);
    if (!updatedAt) {
      return;
    }

    const { agentProvider, variant } = normalizeProviderKey(trade.llmProvider);
    if (!agentProvider) {
      return;
    }

    const key = `${agentProvider}-${variant}`;
    if (!groupedRows.has(key)) {
      groupedRows.set(key, {
        key,
        agentProvider,
        variant,
        returns: [],
        totalTrades: 0,
        winCount: 0,
        notionalPnl: 0,
        updatedAt,
      });
    }

    const row = groupedRows.get(key);
    row.totalTrades += 1;
    row.notionalPnl += trade.pnl;
    row.returns.push(typeof trade.pnlPercentage === 'number' ? trade.pnlPercentage : 0);
    if (trade.pnl > 0) {
      row.winCount += 1;
    }
    if (updatedAt > row.updatedAt) {
      row.updatedAt = updatedAt;
    }
  });

  const rows = Array.from(groupedRows.values())
    .map(row => {
      const averageReturn =
        row.returns.reduce((sum, value) => sum + value, 0) / Math.max(row.returns.length, 1);
      const variance =
        row.returns.reduce((sum, value) => sum + Math.pow(value - averageReturn, 2), 0) /
        Math.max(row.returns.length, 1);
      const standardDeviation = Math.sqrt(variance);
      const sharpeRatio = standardDeviation > 0 ? averageReturn / standardDeviation : 0;
      const winRate = row.totalTrades > 0 ? row.winCount / row.totalTrades : 0;
      const isLiveEligible =
        LIVE_ELIGIBLE_AGENT_PROVIDERS.includes(row.agentProvider) &&
        LIVE_ELIGIBLE_VARIANTS.includes(row.variant);

      return {
        key: row.key,
        agentProvider: row.agentProvider,
        agentName: PROVIDER_DISPLAY_NAMES[row.agentProvider] || row.agentProvider,
        variant: row.variant,
        benchmarkLabel: VARIANT_LABELS[row.variant] || row.variant,
        notionalPnl: row.notionalPnl,
        totalTrades: row.totalTrades,
        winRate,
        sharpeRatio,
        updatedAt: row.updatedAt,
        isLiveEligible,
        metricLabel: formatLiveMetric({
          notionalPnl: row.notionalPnl,
          sharpeRatio,
        }),
        subline: `Resolved ${row.totalTrades} trades · ${(winRate * 100).toFixed(1)}% win rate`,
      };
    })
    .sort((left, right) => {
      if (right.notionalPnl !== left.notionalPnl) {
        return right.notionalPnl - left.notionalPnl;
      }

      return right.updatedAt.getTime() - left.updatedAt.getTime();
    });

  let liveBenchmarkAssigned = false;
  rows.forEach(row => {
    if (!row.isLiveEligible) {
      row.statusLabel = 'Proof only';
      return;
    }

    if (!liveBenchmarkAssigned) {
      row.statusLabel = 'Live benchmark';
      liveBenchmarkAssigned = true;
      return;
    }

    row.statusLabel = 'Live-eligible';
  });

  return rows.slice(0, maxRows);
}

export function buildDisciplineFacts(latestSnapshot) {
  const snapshotTimestamp = formatEasternTimestamp(latestSnapshot?.timestamp);
  const sessionLabel = latestSnapshot?.session || 'unknown';
  const positionCountLabel =
    typeof latestSnapshot?.positionCount === 'number'
      ? `${latestSnapshot.positionCount} positions`
      : 'position count unavailable';

  return {
    schedule: '9:00 AM ET open prep · 3:30 PM ET close prep',
    liveEligibility: 'Gemini 2.5 Pro, Gemini 3 Pro Preview · with_news only',
    brokerEvidence: snapshotTimestamp
      ? `${snapshotTimestamp} · ${sessionLabel} · ${positionCountLabel}`
      : 'No IBKR snapshot published yet',
    cashPilot: 'Manual review · capped small cash payouts after benchmark proof',
  };
}

function coerceDate(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value?.toDate === 'function') {
    return value.toDate();
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}
